import { useState, useEffect } from 'react';
import { Check, Crown, Zap, Star, Loader2, Icon as LucideIcon, X, Infinity } from 'lucide-react';
import {
  usePlans,
  useBillingPeriods,
  useUserSubscriptions, // To know if there's an active sub, might simplify later
  Plan,
  BillingPeriod,
  getUserChannel,
} from '../../services/subscriptionService'; // Adjusted path
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// Icon Mapping (Copied from Suscription.tsx)
const iconMap: { [key: string]: LucideIcon } = {
  default: Zap,
  zap: Zap,
  star: Star,
  crown: Crown,
};

const getPlanIcon = (iconName: string | null): LucideIcon => {
  if (iconName && iconMap[iconName.toLowerCase()]) {
    return iconMap[iconName.toLowerCase()];
  }
  return iconMap.default;
};

interface SubscriptionCreationProps {
  onContinueToPayment: (details: {
    selectedPlanId: number;
    selectedBillingPeriodId: number;
    selectedChannelId: string;
    totalPrice: number;
    autoRenewal: boolean;
    currentPlanDetails?: Plan;
    currentBillingPeriod?: BillingPeriod;
  }) => void;
  // activeSubscription?: any; // Prop to know if user already has a subscription
}

export default function SubscriptionCreation({ onContinueToPayment }: SubscriptionCreationProps) {
  const { t } = useTranslation();
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedBillingPeriodId, setSelectedBillingPeriodId] = useState<number | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [autoRenewalForNewSub, setAutoRenewalForNewSub] = useState(true); // Retained for now

  // Fetching dynamic data
    const { data: channels, isLoading: isLoadingChannels, error: channelsError } = useQuery({
    queryKey: ['userChannels'],
    queryFn: getUserChannel,
    retry: false
  })
  const { data: plansData, isLoading: isLoadingPlans, error: plansError } = usePlans();
  const { data: billingPeriodsData, isLoading: isLoadingBillingPeriods, error: billingPeriodsError } = useBillingPeriods();
console.log("plansData",plansData);

  // This hook is to determine if we should even show this component,
  // Parent component (`SubscriptionPage`) should ideally handle this logic.
  // For now, keeping it to ensure UI consistency with original page.
  const { data: subscriptions, isLoading: isLoadingSubscriptions } = useUserSubscriptions();
  const activeSubscription = subscriptions && subscriptions.length > 0 ? subscriptions[0] : null;

  

  // Effect to set default plan and billing period once data is loaded
  useEffect(() => {
    if (!selectedPlanId && plansData && plansData.length > 0) {
      const popularPlan = plansData.find(p => p.popular) || plansData[0];
      setSelectedPlanId(popularPlan.id);
    }
    if (!selectedBillingPeriodId && billingPeriodsData && billingPeriodsData.length > 0) {
      const yearlyBilling = billingPeriodsData.find(bp => bp.months === 12) || billingPeriodsData[0];
      setSelectedBillingPeriodId(yearlyBilling.id);
    }
  }, [plansData, billingPeriodsData, selectedPlanId, selectedBillingPeriodId]);

  const currentPlanDetails = plansData?.find(p => p.id === selectedPlanId);
  const currentBillingPeriod = billingPeriodsData?.find(b => b.id === selectedBillingPeriodId);

  const basePrice = currentPlanDetails?.price || 0;
  const discountAmount = currentBillingPeriod ? basePrice * currentBillingPeriod.months * (currentBillingPeriod.discountPercent / 100) : 0;
  const totalPrice = currentBillingPeriod ? (basePrice * currentBillingPeriod.months) - discountAmount : basePrice;

  const handleInternalContinue = () => {
    if (!selectedChannelId) {
      toast.error(t('pleaseSelectChannel')); // Simple alert, replace with better UI
      return;
    }
    if (selectedPlanId && selectedBillingPeriodId && currentPlanDetails) {
      onContinueToPayment({
        selectedPlanId,
        selectedBillingPeriodId,
        selectedChannelId,
        totalPrice,
        autoRenewal: autoRenewalForNewSub,
        currentPlanDetails, // Pass these for summary on payment page
        currentBillingPeriod // Pass these for summary on payment page
      });
    } else {
        toast.error(t('pleaseSelectPlanAndBilling'));
    }
  };

  if (isLoadingChannels || isLoadingPlans || isLoadingBillingPeriods || isLoadingSubscriptions) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        <p className="ml-4 text-xl text-gray-700">{t('loadingOptions')}</p>
      </div>
    );
  }

  // If user already has an active subscription, this component shouldn't be shown.
  // This logic should ideally be in the parent, but for self-containment for now:
  // if (activeSubscription) {
  //     return null; // Or a message, or redirect. Parent should handle this.
  // }


  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('chooseYourPerfectPlan')}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('unlockPowerfulFeatures')}
            </p>
          </div>

          {/* Channel Selector */}
          <div className="mb-10 max-w-md mx-auto">
            <label htmlFor="channel-select" className="block text-lg font-medium text-gray-700 mb-2">
              {t('selectYourChannel')}
            </label>
            {isLoadingChannels && <Loader2 className="animate-spin w-5 h-5 mr-2 inline" />}
            {channelsError && <p className="text-red-500">{t('errorLoadingChannels', { error: (channelsError as Error).message })}</p>}
            {channels && channels.length > 0 && (
              <select
                id="channel-select"
                value={selectedChannelId || ''}
                onChange={(e) => setSelectedChannelId(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="" disabled>{t('selectAChannel')}</option>
                {channels.map((channel) => (
                  <option key={channel.id} value={channel.id}>
                    {channel.nameChannel} {/* Ensure this is the correct field for channel name */}
                  </option>
                ))}
              </select>
            )}
            {channels && channels.length === 0 && !isLoadingChannels && (
              <p className="text-gray-600">{t('noChannelsAvailable')}</p>
            )}
          </div>

          {/* Billing Period Selector */}
          {billingPeriodsError && <p className="text-red-500 text-center mb-4">{t('errorLoadingBillingPeriods', { error: (billingPeriodsError as Error).message })}</p>}
          {billingPeriodsData && billingPeriodsData.length > 0 && (
            <div className="flex justify-center mb-12">
              <div className="bg-white p-2 rounded-xl shadow-sm border">
                <div className="flex space-x-1">
                  {billingPeriodsData.map((period) => (
                    <button
                      key={period.id}
                      onClick={() => setSelectedBillingPeriodId(period.id)}
                      className={`relative px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                        selectedBillingPeriodId === period.id
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      {period.label}
                      {period.discountPercent > 0 && (
                        <span className={`absolute -top-2 -right-2 px-2 py-1 text-xs font-bold rounded-full ${
                          selectedBillingPeriodId === period.id
                            ? 'bg-yellow-400 text-yellow-900'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          -{period.discountPercent}%
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {billingPeriodsData && billingPeriodsData.length === 0 && !isLoadingBillingPeriods && (
             <p className="text-center text-gray-600 mb-12">{t('noBillingPeriodsAvailable')}</p>
          )}

          {/* Plans Grid */}
          {plansError && <p className="text-red-500 text-center mb-4">{t('errorLoadingPlans', { error: (plansError as Error).message })}</p>}
          {plansData && plansData.length > 0 && (
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {plansData.map((plan) => {
                const isSelected = selectedPlanId === plan.id;
                const monthlyPrice = plan.price;
                // Ensure currentBillingPeriod is not null before trying to access its properties
                const actualDiscountPercent = currentBillingPeriod ? currentBillingPeriod.discountPercent : 0;
                const discountedPrice = monthlyPrice - (monthlyPrice * (actualDiscountPercent / 100));
                const PlanIconComponent = getPlanIcon(plan.icon);
                const planColor = plan.popular ? 'from-purple-500 to-purple-600' : 'from-blue-500 to-blue-600';

                return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`relative bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 ${
                      isSelected ? 'ring-2 ring-blue-500 shadow-2xl' : ''
                    } ${plan.popular ? 'border-2 border-purple-200' : ''}`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold shadow-lg">
                          {t('mostPopular')}
                        </span>
                      </div>
                    )}
                    <div className="p-8">
                      <div className="text-center mb-6">
                        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${planColor} mb-4`}>
                          <PlanIconComponent className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <p className="text-sm text-gray-500 h-10 mb-2 overflow-hidden">{plan.description}</p>
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-3xl font-bold text-gray-900">€{discountedPrice / 100}</span>
                          <div className="text-left">
                            <div className="text-sm text-gray-600">{t('perMonth')}</div>
                          </div>
                        </div>
                        {currentBillingPeriod && currentBillingPeriod.months > 1 && currentBillingPeriod.discountPercent > 0 && (
                          <p className="text-sm text-green-600 font-medium mt-2">
                            {t('savePerPeriod', { amount: (((monthlyPrice - discountedPrice) * currentBillingPeriod.months) / 100).toFixed(2), period: currentBillingPeriod.label })}
                          </p>
                        )}
                      </div>
                      <div className="space-y-3 mb-8">
                        {(plan.features || []).map((feature, index) => {
                          // Determinar el icono según feature.icon y feature.included
                          let FeatureIcon = Check; // Por defecto Check
                          let iconColor = 'text-green-500';
                          
                          if (!feature.included) {
                            FeatureIcon = X;
                            iconColor = 'text-red-500';
                          } else if (feature.icon) {
                            // Mapear el icono según el valor de feature.icon
                            if (feature.icon.toLowerCase() === 'infinity') {
                              FeatureIcon = Infinity;
                            } else if (feature.icon.toLowerCase() === 'check') {
                              FeatureIcon = Check;
                            }
                          }

                          return (
                            <div key={index} className="flex items-center space-x-3">
                              <FeatureIcon className={`w-5 h-5 ${iconColor} flex-shrink-0`} />
                              <span className={`${feature.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                {feature.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                          isSelected ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {isSelected ? t('selected') : t('selectPlan')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Order Summary & Continue Button */}
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('orderSummary')}</h3>
              <div className="space-y-2 text-sm text-gray-600 mb-4">
                 {selectedChannelId && channels && (
                  <div className="flex justify-between">
                    <span>{t('channelLabel')}</span>
                    <span>{channels.find(c => c.id === selectedChannelId)?.nameChannel}</span> {/* Ensure this is correct channel name field */}
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{currentPlanDetails?.name || 'N/A'} {t('plan')}</span>
                  <span>€{basePrice > 0 ? `${basePrice / 100}${t('perMonth')}` : 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('billingPeriod')}</span>
                  <span>{currentBillingPeriod?.label || 'N/A'}</span>
                </div>
                {currentBillingPeriod && currentBillingPeriod.discountPercent > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t('discount')}</span>
                    <span>-{currentBillingPeriod.discountPercent}%</span>
                  </div>
                )}
              </div>
              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>{t('total')}</span>
                  <span>€{totalPrice > 0 ? totalPrice.toFixed(2) / 100 : 'N/A'}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {t('billed')} {currentBillingPeriod?.label ? (currentBillingPeriod.months === 1 ? t('monthly') : t('everyMonths', { months: currentBillingPeriod.months })) : t('once')}
                </p>
              </div>
            </div>
            {(!plansData || plansData.length === 0) && !isLoadingPlans && (
                <p className="text-center text-gray-600 mb-4">{t('noPlansAvailable')}</p>
            )}
            <button
              onClick={handleInternalContinue}
              disabled={!selectedChannelId || !selectedPlanId || !selectedBillingPeriodId || isLoadingChannels || isLoadingPlans || isLoadingBillingPeriods}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('continueToPayment')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
